// src/components/Presentation/EditableSlateContent.js
import React, { useState, useRef, useEffect } from 'react';
import { Image as ImageIcon, Video, BarChart3, MessageCircle, Users } from 'lucide-react';

const EditableSlateContent = ({ 
  slate, 
  isSelected, 
  onUpdateContent,
  isEditing,
  onStartEditing,
  onStopEditing 
}) => {
  const [isTextEditing, setIsTextEditing] = useState(false);
  const textRef = useRef(null);

  // Focus text editor when entering edit mode
  useEffect(() => {
    if (isTextEditing && textRef.current) {
      textRef.current.focus();
      // Place cursor at end of text
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(textRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [isTextEditing]);

  const handleTextClick = (e) => {
    e.stopPropagation();
    if (slate.type === 'text' && isSelected) {
      setIsTextEditing(true);
      onStartEditing && onStartEditing();
    }
  };

  const handleTextBlur = () => {
    setIsTextEditing(false);
    onStopEditing && onStopEditing();
  };

  const handleTextInput = (e) => {
    const newContent = e.target.textContent;
    onUpdateContent({
      ...slate.content,
      content: newContent
    });
  };

  const handleTextKeyDown = (e) => {
    // Allow Enter for line breaks, Escape to finish editing
    if (e.key === 'Escape') {
      textRef.current.blur();
    }
    // Prevent other keyboard shortcuts from interfering
    e.stopPropagation();
  };

  const renderTextContent = () => {
    const content = slate.content || {};
    const isEmpty = !content.content || content.content.trim() === '';
    
    return (
      <div 
        className={`
          p-4 h-full overflow-hidden flex items-start cursor-text
          ${content.alignment === 'center' ? 'justify-center text-center' : 
            content.alignment === 'right' ? 'justify-end text-right' : 'justify-start text-left'}
        `}
        onClick={handleTextClick}
      >
        <div 
          ref={textRef}
          contentEditable={isSelected && (isTextEditing || isEmpty)}
          suppressContentEditableWarning={true}
          onBlur={handleTextBlur}
          onInput={handleTextInput}
          onKeyDown={handleTextKeyDown}
          className={`
            ${content.fontSize || 'text-lg'} 
            ${content.fontWeight || 'font-normal'}
            ${content.fontFamily || 'font-sans'}
            ${content.fontStyle === 'italic' ? 'italic' : ''}
            ${isEmpty && isSelected ? 'text-gray-400' : ''}
            outline-none min-w-8 min-h-6
          `}
          style={{ 
            color: isEmpty && isSelected ? '#9CA3AF' : (content.textColor || '#000000'),
            textDecoration: content.textDecoration || 'none',
            lineHeight: content.lineHeight || 1.5,
            letterSpacing: `${content.letterSpacing || 0}px`,
            textShadow: content.textShadow || 'none',
            textTransform: content.textTransform || 'none',
            wordBreak: 'break-word',
            hyphens: 'auto'
          }}
        >
          {isEmpty ? (isSelected ? 'Click to add text...' : 'Empty text') : content.content}
        </div>
        
        {/* Editing indicator */}
        {isTextEditing && (
          <div className="absolute top-2 right-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded">
            Editing
          </div>
        )}
      </div>
    );
  };

  const renderImageContent = () => {
    const content = slate.content || {};
    
    if (!content.src) {
      return (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-2" />
            <span className="text-sm">Click to add image</span>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-full overflow-hidden relative">
        <img 
          src={content.src} 
          alt={content.alt || ''} 
          className="w-full h-full transition-all duration-200"
          style={{ 
            objectFit: content.objectFit || 'cover',
            objectPosition: content.position ? 
              `${content.position.x}% ${content.position.y}%` : 'center center',
            transform: `scale(${(content.scale || 100) / 100}) rotate(${content.rotation || 0}deg)`,
            borderRadius: `${content.borderRadius || 0}px`,
            opacity: (content.opacity || 100) / 100,
            transformOrigin: 'center center'
          }}
        />
        
        {/* Image overlay for very low opacity */}
        {content.opacity < 20 && (
          <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-gray-400 bg-gray-50 bg-opacity-50 flex items-center justify-center">
            <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded">
              Low Opacity Image
            </span>
          </div>
        )}
      </div>
    );
  };

  switch (slate.type) {
    case 'text':
      return renderTextContent();
    
    case 'image':
      return renderImageContent();

    case 'video':
      return (
        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
          {slate.content?.src ? (
            <video 
              src={slate.content.src} 
              controls
              autoPlay={slate.content.autoplay}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-white">
              <Video className="w-12 h-12 mx-auto mb-2" />
              <span className="text-sm">Click to add video</span>
            </div>
          )}
        </div>
      );

    case 'poll':
      return (
        <div className="p-4 h-full flex items-center justify-center bg-blue-50 rounded-lg">
          <div className="text-center">
            <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <p className="text-sm text-blue-800 font-medium">
              {slate.content?.question || 'Poll Question'}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {slate.content?.options?.length || 0} options
            </p>
          </div>
        </div>
      );

    case 'chart':
      return (
        <div className="p-4 h-full flex items-center justify-center bg-purple-50 rounded-lg">
          <div className="text-center">
            <BarChart3 className="w-8 h-8 mx-auto text-purple-600 mb-2" />
            <p className="text-sm text-purple-800 font-medium">
              {slate.content?.title || 'Chart Title'}
            </p>
            <p className="text-xs text-purple-600 mt-1">
              {slate.content?.type || 'bar'} chart
            </p>
          </div>
        </div>
      );

    case 'question':
      return (
        <div className="p-4 h-full flex items-center justify-center bg-green-50 rounded-lg">
          <div className="text-center">
            <MessageCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <p className="text-sm text-green-800 font-medium">Q&A</p>
            <p className="text-xs text-green-600 mt-1">
              {slate.content?.prompt || 'Ask your audience...'}
            </p>
          </div>
        </div>
      );

    default:
      return (
        <div className="h-full flex items-center justify-center bg-red-100 rounded-lg">
          <div className="text-center">
            <span className="text-sm text-red-600">Unknown: {slate.type}</span>
          </div>
        </div>
      );
  }
};

export default EditableSlateContent;